// © Microsoft Corporation. All rights reserved.

import { ChildExperienceCMSConfigInfo, ChildExperienceGitConfigInfo, ConfigReference } from "@msnews/config-data-types";

import { ExperienceState } from "./ExperienceState";
import { identity } from "lodash-es";

/**
 * Core config structure used to reference another experience config instance.
 * Note that this currently has support for both CMS & Git based config IDs.
 * Once the Git config migration is 100% complete, we must remove old CMS ID support.
 */
export type ChildExperienceConfigInfo =
    (ChildExperienceCMSConfigInfo | ChildExperienceGitConfigInfo)
    & ChildExperienceConfigInfoState;

/**
 * Interim type used to represent either a CMS HREF string or a Git config info structure.
 * This is used to support the upgrade process from one-off CMS HREF references in various existing experience
 * configs to the propertly formed configRef structure for Git configs.
 *
 * This should be removed and replaced with just the Git config structure after 100% Git config migration.
 */
export type ChildExperienceHybridConfigInfo = string | ChildExperienceGitConfigInfo;

/**
 * This represents a universal type that can represent one of the following three config info types:
 * - RAW CMS HREF string
 * - CMS config info structure
 * - Git config info structure
 *
 * This must be removed and replaced with the Git config info structure after 100% Git config migration.
 */
export type ChildExperienceUniversalConfigInfo =
    ChildExperienceCMSConfigInfo |
    ChildExperienceGitConfigInfo
    | string;

/**
 * Auxiliary info regarding the child experience's current loading state.
 * This info is normally set dynamically during run time and is not part
 * of the base config structure.
 */
export interface ChildExperienceConfigInfoState {
    /**
     * optional experience state
     */
    state?: ExperienceState;
}

/**
 * Temporary helper to conditionally extract the experience type from either a CMS or Git config info structure.
 * The experience type will be returned in case of a CMS or Git config ref structure, or null, in case of a CMS HREF
 * This should be removed after 100% Git config migration.
 * @param configInfo - The config info structure for either a CMS or Git based ref
 * @returns the experience type for the config reference.
 */
export function getExperienceType(configInfo: ChildExperienceConfigInfo): string | null {
    return evalGitOrCmsExperienceInfo(
        configInfo,
        gitId => gitId.configRef.experienceType,
        cmsId => cmsId.experienceType,
        null
    );
}

/**
 * Temporary helper to conditionally extract the ID from either a CMS or Git config info structure, or CMS HREF.
 * This should be removed after 100% Git config migration.
 * @param configInfo - The config info structure for either a CMS or Git based ref, or CMS HREF
 * @returns A {string} if the given config ID is either a CMS config info or HREF,
 *  or an {ConfigReference} in case the given config ID is a Git ID.
 */
export function getConfigId(configInfo: ChildExperienceUniversalConfigInfo): string | ConfigReference {
    return evalGitOrCmsExperienceInfo(
        configInfo,
        gitRef => gitRef.configRef,
        cmsRef => cmsRef.href,
        cmsHref => cmsHref
    );
}

/**
 * Temporary helper to conditionally extract the HREF from either a CMS or Git config info structure, or CMS HREF.
 * This should be removed after 100% Git config migration.
 * @param configInfo - The config info structure for either a CMS or Git based ref, or CMS HREF
 * @returns A {string} representing the CMS HREF only if the given config ID is either a CMS config info or HREF.
 *  In case the given config ID is a Git ID, null will be returned instead.
 */
export function getConfigHref(configInfo: ChildExperienceUniversalConfigInfo): string | null {
    return evalGitOrCmsExperienceInfo(
        configInfo,
        null,
        cmsRef => cmsRef.href,
        cmsHref => cmsHref
    );
}

/**
 * Temporary helper to conditionally extract the Git ID from a CMS HREF or Git config ref structure.
 * This should be removed after 100% Git config migration.
 * @param configInfo - The config info structure for either a Git based ref, or CMS HREF
 * @returns A {ChildExperienceGitConfigInfo} if the given config ID is a Git config ref,
 *  or null in case the given config ID is a CMS HREF.
 */
export function getConfigInfo(configInfo: ChildExperienceHybridConfigInfo): ChildExperienceGitConfigInfo | null {
    return evalGitOrCmsExperienceInfo(
        configInfo,
        identity,
        null,
        null
    );
}

/**
 * Temporary helper to unify converting the given config ID to a string representation.
 * This should be removed after 100% Git config migration.
 * @param configInfo - The config info for a CMS or Git config
 * @param instanceIdOverride - Optional instance ID override to use instead of the one in the config object
 * @returns A string that uniquely identifies the given config info object
 */
export function getConfigIdString(configInfo: ChildExperienceUniversalConfigInfo, instanceIdOverride?: string): string {
    return evalGitOrCmsExperienceInfo(
        configInfo,
        gitConfigInfo => `${getConfigRefString(gitConfigInfo.configRef)}_${instanceIdOverride || gitConfigInfo.instanceId || ""}`,
        cmsConfigInfo => `${cmsConfigInfo.experienceType}_${cmsConfigInfo.href}_${instanceIdOverride || cmsConfigInfo.instanceId || ""}`,
        cmsHref => `${cmsHref}_${instanceIdOverride || ""}`
    );
}

/**
 * Helper to convert a Git ID structure to a string representation.
 * @param configRef - The Git config ID
 * @returns A string that uniquely identifies the given Git config ID
 */
export function getConfigRefString(configRef: ConfigReference): string {
    return `${configRef.experienceType}_${configRef.instanceSrc}_${configRef.sharedNs || ""}`;
}

/**
 * Helper to conditionally check if the given a CMs/Git ID structure is valid
 * @param configInfo - The config info object to check
 * @returns True if the config info structure contains all the required fields.
 */
export function isValidConfigInfo(configInfo: ChildExperienceConfigInfo): boolean {
    return configInfo
        && configInfo.instanceId != null
        && !!evalGitOrCmsExperienceInfo(
            configInfo,
            gitInfo => gitInfo.configRef && gitInfo.configRef.experienceType && gitInfo.configRef.instanceSrc,
            cmsInfo => cmsInfo.experienceType && cmsInfo.href != null,
            null);
}

/**
 * Core helper to conditionally process/evaluate all three types of config IDs
 * including CMS HREF, CMS config info, or Git config info. Based on the given
 * config info type, one of the given callbacks will be invoked and the result
 * from the callback will be returned.
 * @param configInfo - The config info object to process
 * @param processGitConfigInfo - Processor callback for Git config info objects
 * @param processCMSConfigInfo - Processor callback for CMS config info objects
 * @param processCMSConfigHref - Processor callback for CMS HREF strings
 * @param defaultValue - The default value to return in case the config info
 *  is invalid or does not conform to any of the known ID types.
 * @returns The conditional return value from one of the specified callbacks
 *  based on the detected config info type.
 */
export function evalGitOrCmsExperienceInfo<T1, T2, T3>(
    configInfo: ChildExperienceUniversalConfigInfo,
    processGitConfigInfo?: (configRef: ChildExperienceGitConfigInfo) => T1,
    processCMSConfigInfo?: (cmsRef: ChildExperienceCMSConfigInfo) => T2,
    processCMSConfigHref?: (cmsHref: string) => T3,
    defaultValue?: T1 | T2 | T3
): T1 | T2 | T3 | null {

    if (defaultValue === undefined) {
        defaultValue = null;
    }

    if (configInfo != null) {

        // process CMS HREF string
        if (typeof configInfo === "string") {
            return processCMSConfigHref && processCMSConfigHref(configInfo) || defaultValue;
        }

        // process Git config info object
        const gitRef = (configInfo as ChildExperienceGitConfigInfo);
        if (gitRef.configRef) {
            return processGitConfigInfo && processGitConfigInfo(gitRef) || defaultValue;
        }

        // process CMS config info object
        const cmsRef = configInfo as ChildExperienceCMSConfigInfo;
        if (processCMSConfigInfo) {
            return processCMSConfigInfo(cmsRef) || defaultValue;
        }
    }

    return defaultValue;
}
